import { LoaderCircleV2, SkeletonLoader, UI_CAPTION, UI_CAPTION_BOLD, UI_BODY_BOLD, colors2, DropdownSingle } from "oolib";
import React, { useState, useMemo, Fragment } from "react";
import { useQuery } from "react-query";
import axios from "axios";
import styled from "styled-components";
import { DataVizWidgetShell } from "../../components/analysisModules/reports/ReportLanding/comps/WidgetRenderer/comps/DataVizWidgetShell";
import { LineChart } from "@/components/dataviz/LineChartV2";

// --- Constants ---

const INTERVAL_OPTIONS = [
  { value: "hour", display: "Hourly" },
  { value: "day", display: "Daily" },
  { value: "week", display: "Weekly" },
  { value: "month", display: "Monthly" },
  { value: "year", display: "Yearly" },
];

const VIEW_BASE_OPTIONS = [
  { value: "gainers", display: "Biggest Risers" },
  { value: "losers", display: "Biggest Decliners" },
];

const ALL_GROUP = { value: "__all__", display: "All Themes" };

const INTERVAL_PERIOD_LABEL = {
  hour: "since last hour",
  day: "since yesterday",
  week: "since last week",
  month: "since last month",
  year: "since last year",
};

// --- Helpers ---

const formatDate = (isoStr) => {
  if (!isoStr) return "—";
  const d = new Date(isoStr);
  const now = new Date();
  const diffMs = now - d;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
  return `${Math.floor(diffDays / 365)} years ago`;
};

// --- Shared data fetching ---

const fetchTagHealth = async ({ queryKey }) => {
  const [,, { interval, group }] = queryKey;
  const params = new URLSearchParams({ interval });
  if (group && group !== "__all__") params.set("group", group);
  const res = await axios.get(`/api/playground/dashboardTagHealth?${params}`);
  return res.data;
};

const useTagHealthQuery = ({ widget, interval, group }) =>
  useQuery({
    queryKey: ["tagHealth", widget, { interval, group }],
    queryFn: fetchTagHealth,
    staleTime: 5 * 60 * 1000,
    keepPreviousData: true,
  });

// --- Sub-components ---

const RefetchOverlay = () => (
  <div style={{
    position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
    background: "rgba(255,255,255,0.6)", zIndex: 2, borderRadius: 8,
  }}>
    <LoaderCircleV2 />
  </div>
);

const MoverDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  position: relative;
  z-index: 1;
  flex-shrink: 0;
`;

const MoverEntry = ({ tag, color, intervalValue, isLast }) => {
  const hierarchy = [tag.theme, tag.subTheme].filter(Boolean).join(" > ");
  const periodLabel = INTERVAL_PERIOD_LABEL[intervalValue] || "vs. earlier period";
  const isNew = tag.pctChange === null;
  const pctColor = isNew ? colors2.grey50 : tag.pctChange > 0 ? "#22c55e" : "#ef4444";

  let changeText;
  if (isNew) {
    changeText = `New this ${intervalValue || "period"} — no prior usage`;
  } else {
    const pctAbs = Math.abs(tag.pctChange);
    const direction = tag.pctChange > 0 ? "increase" : "decrease";
    changeText = `${pctAbs.toLocaleString()}% usage ${direction} ${periodLabel}`;
  }

  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0", position: "relative" }}>
      <div style={{ paddingTop: 4, flexShrink: 0, width: 10 }}>
        <MoverDot $color={color} />
        {!isLast && (
          <div style={{ position: "absolute", top: 18, left: 4, width: 2, height: "100%", background: colors2.grey10 }} />
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <UI_BODY_BOLD style={{ fontSize: 13 }}>{tag.display}</UI_BODY_BOLD>
        {hierarchy && (
          <UI_CAPTION color={colors2.grey50} style={{ marginTop: 2 }}>{hierarchy}</UI_CAPTION>
        )}
        <UI_CAPTION color={pctColor} style={{ marginTop: 3 }}>
          {changeText}
        </UI_CAPTION>
        <UI_CAPTION color={colors2.grey30} style={{ marginTop: 2 }}>
          Last used {formatDate(tag.lastUsed)} · {tag.count.toLocaleString()} total uses
        </UI_CAPTION>
      </div>
    </div>
  );
};

const MoversColumn = ({ title, tags, color, emptyText, intervalValue }) => (
  <Fragment>
    <UI_CAPTION_BOLD style={{ paddingBottom: 8, textTransform: "uppercase", letterSpacing: "0.5px", flexShrink: 0, position: 'sticky', top: 0, background: colors2.white, zIndex: 2 }} color={colors2.grey50}>
      {title}
    </UI_CAPTION_BOLD>
    {tags.length > 0 ? (
      <Fragment>
        {tags.map((tag, i) => (
          <MoverEntry key={tag.tagId} tag={tag} color={color} intervalValue={intervalValue} isLast={i === tags.length - 1} />
        ))}
      </Fragment>
    ) : (
      <UI_CAPTION color={colors2.grey30} style={{ padding: "12px 0" }}>{emptyText}</UI_CAPTION>
    )}
  </Fragment>
);

const TagHealthSkeleton = () => (
  <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start", marginTop: "1.5rem" }}>
    <DataVizWidgetShell title="Tag Freshness" disableAccordion style={{ flex: 1, minWidth: 0 }}>
      <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 12 }}>
        <SkeletonLoader style={{ height: 8, borderRadius: 4 }} />
        <SkeletonLoader style={{ height: 16, width: "60%" }} />
        <SkeletonLoader style={{ height: 200 }} />
      </div>
    </DataVizWidgetShell>
    <DataVizWidgetShell title="Tag Usage Trends" disableAccordion style={{ flex: 1, minWidth: 0 }}>
      <div style={{ padding: "0 16px 16px" }}>
        <SkeletonLoader style={{ height: 400 }} />
      </div>
    </DataVizWidgetShell>
  </div>
);

// --- Main Component ---

export const TagHealth = () => {
  const [freshnessInterval, setFreshnessInterval] = useState({ value: "month", display: "Monthly" });
  const [trendsInterval, setTrendsInterval] = useState({ value: "month", display: "Monthly" });
  const [freshnessGroup, setFreshnessGroup] = useState(ALL_GROUP);
  const [trendsGroup, setTrendsGroup] = useState(ALL_GROUP);
  const [view, setView] = useState(VIEW_BASE_OPTIONS[0]);

  const freshnessQuery = useTagHealthQuery({ widget: "freshness", interval: freshnessInterval.value, group: freshnessGroup.value });
  const trendsQuery = useTagHealthQuery({ widget: "trends", interval: trendsInterval.value, group: trendsGroup.value });

  const groupOptions = useMemo(() => {
    const groups = freshnessQuery.data?.availableGroups || trendsQuery.data?.availableGroups;
    if (!groups) return [ALL_GROUP];
    return [ALL_GROUP, ...groups.map((g) => ({ value: g, display: g }))];
  }, [freshnessQuery.data?.availableGroups, trendsQuery.data?.availableGroups]);

  // Initial load — show full skeleton
  if (freshnessQuery.isLoading && !freshnessQuery.data) return <TagHealthSkeleton />;
  if (freshnessQuery.isError) return <UI_CAPTION color={colors2.red}>Failed to load tag health data</UI_CAPTION>;
  if (!freshnessQuery.data) return null;

  const data = freshnessQuery.data;
  const trendsData = trendsQuery.data || data;
  const trendLines = view.value === "losers" ? trendsData.loserTrends : trendsData.gainerTrends;
  const freshnessRefetching = freshnessQuery.isFetching && !!freshnessQuery.data;
  const trendsRefetching = trendsQuery.isFetching && !!trendsQuery.data;

  const freshnessFilterBar = (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
      <div style={{ width: 180 }}>
        <DropdownSingle value={freshnessGroup} onChange={(id, val) => setFreshnessGroup(val)} options={groupOptions} S />
      </div>
      <div style={{ width: 130 }}>
        <DropdownSingle value={freshnessInterval} onChange={(id, val) => setFreshnessInterval(val)} options={INTERVAL_OPTIONS} S />
      </div>
    </div>
  );

  const trendsFilterBar = (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }} onClick={(e) => e.stopPropagation()}>
      <div style={{ width: 180 }}>
        <DropdownSingle value={trendsGroup} onChange={(id, val) => setTrendsGroup(val)} options={groupOptions} S />
      </div>
      <div style={{ width: 170 }}>
        <DropdownSingle value={view} onChange={(id, val) => setView(val)} options={VIEW_BASE_OPTIONS} S />
      </div>
      <div style={{ width: 130 }}>
        <DropdownSingle value={trendsInterval} onChange={(id, val) => setTrendsInterval(val)} options={INTERVAL_OPTIONS} S />
      </div>
    </div>
  );

  const WIDGET_HEIGHT = 460;

  return (
    <div style={{ display: "flex", gap: "1rem", alignItems: "stretch", marginTop: "1.5rem" }}>
      {/* Left: Tag Freshness */}
      <div style={{ flex: 1, minWidth: 0, position: "relative", maxHeight: WIDGET_HEIGHT }}>
        {freshnessRefetching && <RefetchOverlay />}
        <DataVizWidgetShell
          title="Tag Freshness"
          disableAccordion
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
          customHeaderRightComp={freshnessFilterBar}
          widgetWrapperStyle={{minHeight:0}}
        >
          <div style={{ flex: 1, height: "100%" }}>
            <div style={{ display: "flex", height: "100%" }}>
              <div style={{padding:"0 16px", overflow: 'auto', flex: 1, minWidth: 0,}}>
              <MoversColumn
                title="Rising"
                tags={data.gainers}
                color={colors2.green}
                emptyText="No rising tags in this period"
                intervalValue={freshnessInterval.value}
              />
              </div>
              <div style={{ width: 1, background: colors2.grey10, flexShrink: 0 }} />
              <div style={{padding:"0 16px", overflow: 'auto', flex: 1, minWidth: 0,}}>
              <MoversColumn
                title="Declining"
                tags={data.losers}
                color={colors2.red}
                emptyText="No declining tags in this period"
                intervalValue={freshnessInterval.value}
              />
              </div>
            </div>
          </div>
        </DataVizWidgetShell>
      </div>

      {/* Right: Tag Usage Trends */}
      <div style={{ flex: 1, minWidth: 0, position: "relative", maxHeight: WIDGET_HEIGHT }}>
        {trendsRefetching && <RefetchOverlay />}
        <DataVizWidgetShell
          title="Tag Usage Trends"
          disableAccordion
          style={{ height: "100%" }}
          customHeaderRightComp={trendsFilterBar}
        >
          {trendLines?.length > 0 && trendLines[0]?.dimensions?.length > 0 ? (
            <div style={{ width: "100%", height: 400 }}>
              <LineChart
                data={trendLines}
                interval={trendsInterval.value}
                responsive={true}
                downsample={true}
                height={400}
                style={{ minHeight: 0 }}
              />
            </div>
          ) : (
          <UI_CAPTION color={colors2.grey50} style={{ padding: "1rem" }}>
            {view.value === "losers"
              ? "No declining tags in this period"
              : "No rising tags in this period"}
          </UI_CAPTION>
        )}
        </DataVizWidgetShell>
      </div>
    </div>
  );
};
